/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Button, Card, Theme } from '@nexigendigital/nxui'
import { shopAPI, useCheckoutCartMutation, useUpdateCartItemsMutation } from 'api/shop'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { clearItems } from 'store/slices/shopSlice'

import CartItems from 'containers/shop/cart/cartItems'
import BillingDetails from 'containers/shop/checkout/billingDetails'
import CartEmpty from 'containers/shop/cart/cartEmpty'
import OrderTotal from 'containers/shop/checkout/orderTotal'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CheckoutConfirmation() {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { cart, items, paymentMethod } = useAppSelector((state) => state.shop)
    const { data } = shopAPI.endpoints.paymentMethods.useQueryState(undefined)
    const [checkoutCart, { isLoading, isSuccess }] = useCheckoutCartMutation()
    const [updateCartItems] = useUpdateCartItemsMutation()

    useEffect(() => {
        if (isSuccess) {
            navigate('/order')
        }
    }, [isSuccess])

    function Footer() {
        return (
            <Anchor
                color={'error'}
                onClick={() => {
                    if (cart?.uuid) {
                        updateCartItems({ uuid: cart.uuid, remove_items: cart.items.map(({ item_uuid }) => item_uuid) })
                        dispatch(clearItems())
                    }
                }}>
                Clear Cart
            </Anchor>
        )
    }

    return (
        <StyledCheckoutConfirmation>
            <Card
                title={'Cart'}
                footer={items.length === 0 ? '' : <Footer/>}
            >
                {items.length === 0 ? <CartEmpty/> : <CartItems/>}
            </Card>
            <Order>
                <BillingDetails/>
                <OrderTotal/>
                <Button type={'button'} color={'primary'} loading={isLoading} onClick={() => {
                    if (cart?.uuid && paymentMethod?.id && data) {
                        const creditCard = data.find(({ name }: { name: string }) => name === 'Credit Card')

                        checkoutCart({
                            uuid: cart.uuid,
                            payment_method_id: creditCard?.id,
                            payment_method_data_id: paymentMethod.id
                        })
                    }
                }}>
                    Checkout
                </Button>
            </Order>
        </StyledCheckoutConfirmation>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledCheckoutConfirmation = styled.div`
    display: flex;
    gap: 24px;

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        gap: 0;
    }

    article {
        width: 100%;
        margin-bottom: 0;
        
        &:first-of-type {
            ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                margin-top: 20px
            }
        }

        > div {
            justify-content: center;
        }
    }

    footer {
        padding: 0 0 18px 24px;
    }
`

const Order = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    > button {
        width: 100%;
    }
`
